<template>
  <div class="dashboard-layout">
    <!-- Sidebar Component -->
    <Sidebar />

    <!-- Main Content Area -->
    <div class="dashboard-content">
      <!-- Enhanced Header Section -->
      <header class="dashboard-header">
        <div class="header-content">
          <div class="header-left">
            <div class="header-title">
              <h1>
                <span class="greeting-text">{{ greetingMessage }}</span>
                <span class="user-name">{{ userName }}</span>
              </h1>
              <p class="header-subtitle">{{ motivationalMessage }}</p>
            </div>
          </div>
          <div class="header-right">
            <div class="header-info">
              <div class="weather-widget">
                <div class="weather-icon">🌤️</div>
                <span class="weather-temp">22°C</span>
              </div>
              <div class="date-display">
                <div class="date-primary">{{ currentDate.day }}</div>
                <div class="date-secondary">
                  <span>{{ currentDate.month }}</span>
                  <span>{{ currentDate.year }}</span>
                </div>
              </div>
            </div>
            <div class="user-profile" @click="toggleUserMenu">
              <div class="avatar-container">
                <img :src="userAvatar" :alt="userName" class="avatar" />
                <div class="status-indicator"></div>
              </div>
              <div class="user-info">
                <span class="user-name-small">{{ userName }}</span>
                <span class="user-role">Administrator</span>
              </div>
              <div class="dropdown-arrow" :class="{ 'rotated': showUserMenu }">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Quick Stats Bar -->
        <div class="quick-stats">
          <div class="stat-item">
            <div class="stat-icon">📊</div>
            <div class="stat-content">
              <span class="stat-value">{{ totalItems }}</span>
              <span class="stat-label">Total Items</span>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon">⚡</div>
            <div class="stat-content">
              <span class="stat-value">{{ activeProjects }}</span>
              <span class="stat-label">Active Projects</span>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon">✅</div>
            <div class="stat-content">
              <span class="stat-value">{{ completedTasks }}</span>
              <span class="stat-label">Completed Today</span>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon">🎯</div>
            <div class="stat-content">
              <span class="stat-value">{{ efficiency }}%</span>
              <span class="stat-label">Efficiency</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Dashboard Component with Enhanced Container -->
      <main class="dashboard-main">
        <Dashboard />
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue';
import Dashboard from '@/components/Dashboard.vue';
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'DashboardView',
  components: {
    Sidebar,
    Dashboard
  },
  setup() {
    const store = useStore();
    const currentDate = ref({});
    const showUserMenu = ref(false);

    // Mock data for quick stats (replace with real data from store)
    const totalItems = ref(248);
    const activeProjects = ref(12);
    const completedTasks = ref(8);
    const efficiency = ref(94);

    // Get user info from store
    const userName = computed(() => store.getters['auth/getUserName'] || 'Admin User');
    const userAvatar = computed(() => {
      const user = store.getters['auth/getUser'];
      return user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName.value)}&background=6366F1&color=fff&size=128`;
    });

    const greetingMessage = computed(() => {
      const hour = new Date().getHours();
      if (hour < 12) return 'Good morning,';
      if (hour < 17) return 'Good afternoon,';
      return 'Good evening,';
    });

    const motivationalMessage = computed(() => {
      const messages = [
        'Ready to make today productive?',
        'Let\'s achieve great things today!',
        'Your dashboard is looking great!',
        'Time to tackle your goals!',
        'Every moment is a fresh beginning.'
      ];
      return messages[Math.floor(Math.random() * messages.length)];
    });

    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value;
    };

    const formatDate = () => {
      const now = new Date();
      currentDate.value = {
        day: now.getDate().toString().padStart(2, '0'),
        month: now.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
        year: now.getFullYear().toString(),
        full: now.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long', 
          day: 'numeric'
        })
      };
    };

    onMounted(() => {
      formatDate();
      
      // Load any required data
      store.dispatch('dashboard/loadInitialData');
      
      // Update date every minute
      setInterval(formatDate, 60000);
    });

    return {
      currentDate,
      userName,
      userAvatar,
      showUserMenu,
      greetingMessage,
      motivationalMessage,
      totalItems,
      activeProjects,
      completedTasks,
      efficiency,
      toggleUserMenu
    };
  }
};
</script>

<style scoped>
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow-x: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.dashboard-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: calc(100% - 280px);
  overflow-x: hidden;
  min-width: 0;
}

/* Enhanced Glassmorphism Header */
.dashboard-header {
  position: sticky;
  top: 0;
  z-index: 50;
  backdrop-filter: blur(20px) saturate(180%);
  background: rgba(255, 255, 255, 0.85);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border-radius: 0 0 24px 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  gap: 2rem;
}

.header-left {
  flex: 1;
}

.header-title h1 {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.greeting-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.user-name {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  background-size: 200% 200%;
  animation: gradientShift 4s ease-in-out infinite;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.header-subtitle {
  color: #64748b;
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.8;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.weather-widget {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.weather-icon {
  font-size: 1.25rem;
}

.weather-temp {
  font-weight: 600;
  color: #334155;
  font-size: 0.875rem;
}

.date-display {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.date-primary {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.date-secondary {
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.user-profile:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.avatar-container {
  position: relative;
}

.avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.status-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  background: #10b981;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.2);
}

.user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-name-small {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.dropdown-arrow {
  color: #64748b;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

/* Quick Stats Bar */
.quick-stats {
  display: flex;
  gap: 1rem;
  padding: 1rem 2rem 1.5rem;
  overflow-x: auto;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  min-width: 140px;
  white-space: nowrap;
}

.stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.stat-icon {
  font-size: 1.25rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  margin-top: 0.125rem;
}

/* Main Dashboard Content */
.dashboard-main {
  flex: 1;
  padding: 1.5rem 2rem;
  background: rgba(241, 245, 249, 0.5);
  min-height: calc(100vh - 200px);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header-info {
    gap: 1rem;
  }
  
  .weather-widget {
    display: none;
  }
  
  .quick-stats {
    gap: 0.75rem;
  }
  
  .stat-item {
    min-width: 120px;
  }
}

@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 240px;
    width: calc(100% - 240px);
  }
  
  .header-content {
    padding: 1rem 1.5rem;
  }
  
  .quick-stats {
    padding: 1rem 1.5rem 1.5rem;
  }
  
  .dashboard-main {
    padding: 1rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    width: 100%;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  
  .header-left,
  .header-right {
    width: 100%;
  }
  
  .header-right {
    justify-content: space-between;
  }
  
  .header-info {
    flex: 1;
  }
  
  .date-display {
    display: none;
  }
  
  .user-info {
    display: none;
  }
  
  .quick-stats {
    padding: 0.5rem 1rem 1rem;
    gap: 0.5rem;
  }
  
  .stat-item {
    min-width: 100px;
    padding: 0.5rem 0.75rem;
  }
  
  .dashboard-main {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .greeting-text {
    font-size: 1rem;
  }
  
  .header-title h1 {
    font-size: 1.5rem;
  }
  
  .header-subtitle {
    display: none;
  }
  
  .stat-item {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
    min-width: 80px;
  }
  
  .stat-icon {
    width: 1.5rem;
    height: 1.5rem;
    font-size: 1rem;
  }
}

/* Enhanced animations and transitions */
@keyframes slideInFromTop {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dashboard-header {
  animation: slideInFromTop 0.6s ease-out;
}

.stat-item {
  animation: slideInFromTop 0.6s ease-out;
}

.stat-item:nth-child(1) { animation-delay: 0.1s; }
.stat-item:nth-child(2) { animation-delay: 0.2s; }
.stat-item:nth-child(3) { animation-delay: 0.3s; }
.stat-item:nth-child(4) { animation-delay: 0.4s; }

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .dashboard-layout {
    background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%);
  }
  
  .dashboard-header {
    background: rgba(15, 23, 42, 0.85);
    border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  }
  
  .greeting-text,
  .header-subtitle {
    color: #94a3b8;
  }
  
  .stat-item,
  .date-display,
  .weather-widget,
  .user-profile {
    background: rgba(15, 23, 42, 0.8);
    border: 1px solid rgba(148, 163, 184, 0.1);
  }
  
  .stat-value,
  .user-name-small,
  .date-primary {
    color: #f1f5f9;
  }
  
  .dashboard-main {
    background: rgba(15, 23, 42, 0.5);
  }
}
</style> 